import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, Download, Trash2, Link2, MessageCircle, BellRing, Loader2 } from "lucide-react";
import { api, errMsg, useApi, useRefresh } from "@/lib/api";
import { waLink } from "@/lib/crm";

const openFile = async (f) => {
  try {
    const r = await api.get(`/files/${f.file_id}`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) { toast.error("Download non riuscito"); }
};

export const PracticeFiles = ({ practice, docs, onDocReceived }) => {
  const pid = practice.practice_id;
  const { data: files = [], refetch } = useApi(`/practices/${pid}/files`);
  const { data: client } = useApi(`/clients/${practice.client_id}`);
  const refresh = useRefresh();
  const [busy, setBusy] = useState(null);
  const [link, setLink] = useState("");

  useEffect(() => { if (practice.new_uploads) api.post(`/practices/${pid}/uploads-seen`).then(refresh); }, [pid, practice.new_uploads, refresh]);

  const getLink = async () => {
    if (link) return link;
    const { data } = await api.post(`/practices/${pid}/upload-link`, { public_base: window.location.origin });
    const l = `${window.location.origin}/documenti/${data.token}`;
    setLink(l);
    return l;
  };
  const copyLink = async () => {
    const l = await getLink();
    try { await navigator.clipboard.writeText(l); } catch { window.prompt("Copia il link", l); }
    toast.success("Link per il caricamento copiato");
  };
  const shareWa = async () => {
    const l = await getLink();
    const missing = docs.filter((d) => !d.received).map((d) => `- ${d.name}`).join("\n");
    window.open(waLink(client?.phone, `Buongiorno ${client?.first_name || ""}, per proseguire con la pratica può caricare qui i documenti:\n${l}\n\nMi servono:\n${missing}`), "_blank");
  };
  const remind = async () => {
    setBusy("remind");
    try { const { data } = await api.post(`/practices/${pid}/doc-reminder`, { public_base: window.location.origin }); toast.success(`Sollecito inviato (${data.missing} documenti mancanti)`); refresh(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };
  const upload = async (idx, file) => {
    if (!file) return;
    setBusy(`up-${idx}`);
    const fd = new FormData();
    fd.append("file", file);
    if (idx !== null) fd.append("doc_index", idx);
    try { await api.post(`/practices/${pid}/files`, fd); toast.success("File caricato"); refetch(); if (idx !== null) onDocReceived(idx); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };
  const remove = async (f) => { await api.delete(`/files/${f.file_id}`); refetch(); };

  return (
    <div className="sm:col-span-2 rounded-xl border border-slate-200 p-4 space-y-3" data-testid="practice-files">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-display font-semibold">File caricati ({files.length})</p>
        <div className="flex flex-wrap gap-1.5">
          <button type="button" data-testid="upload-link-copy" onClick={copyLink} className="flex items-center gap-1.5 rounded-full border border-slate-200 px-3 py-1.5 text-xs hover:border-sky-500 hover:text-sky-700 transition-colors"><Link2 className="h-3.5 w-3.5" />Link per il cliente</button>
          <button type="button" data-testid="upload-link-wa" onClick={shareWa} disabled={!client?.phone} className="flex items-center gap-1.5 rounded-full border border-slate-200 px-3 py-1.5 text-xs hover:border-emerald-500 hover:text-emerald-700 disabled:opacity-40 transition-colors"><MessageCircle className="h-3.5 w-3.5" />Invia su WhatsApp</button>
          <button type="button" data-testid="doc-reminder-btn" onClick={remind} disabled={busy === "remind" || !client?.email} title={client?.email ? "" : "Il cliente non ha email"} className="flex items-center gap-1.5 rounded-full bg-amber-500 text-white px-3 py-1.5 text-xs hover:bg-amber-600 disabled:opacity-40 transition-colors">{busy === "remind" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <BellRing className="h-3.5 w-3.5" />}Sollecita via email</button>
        </div>
      </div>
      {practice.last_doc_reminder_at && <p className="text-[11px] text-slate-400">Ultimo sollecito: {new Date(practice.last_doc_reminder_at).toLocaleString("it-IT")} · inviati {practice.doc_reminder_count || 0}</p>}
      <div className="space-y-1.5">
        {docs.map((d, i) => (
          <div key={`${d.name}-${i}`} className="flex items-center gap-2 text-sm">
            <span className={`flex-1 truncate ${d.received ? "text-emerald-700" : "text-slate-600"}`}>{d.name}</span>
            <label data-testid={`doc-upload-${i}`} className="cursor-pointer flex items-center gap-1 text-xs text-sky-700 hover:underline">
              {busy === `up-${i}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}Carica
              <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif" className="hidden" onChange={(e) => { upload(i, e.target.files?.[0]); e.target.value = ""; }} />
            </label>
          </div>
        ))}
      </div>
      {files.length > 0 && (
        <ul className="divide-y divide-slate-100 border-t border-slate-100 pt-1" data-testid="practice-files-list">
          {files.map((f) => (
            <li key={f.file_id} className="flex items-center gap-2 py-2 text-sm">
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium">{f.original_filename}</p>
                <p className="text-[11px] text-slate-400">{f.doc_name} · {f.uploaded_by === "cliente" ? "dal cliente" : "da te"} · {new Date(f.created_at).toLocaleDateString("it-IT")} · {(f.size / 1024 / 1024).toFixed(1)} MB</p>
              </div>
              <button type="button" data-testid={`file-open-${f.file_id}`} onClick={() => openFile(f)} className="h-8 w-8 grid place-items-center rounded-full hover:bg-sky-50 text-sky-700"><Download className="h-4 w-4" /></button>
              <button type="button" data-testid={`file-delete-${f.file_id}`} onClick={() => remove(f)} className="h-8 w-8 grid place-items-center rounded-full hover:bg-rose-50 text-rose-600"><Trash2 className="h-4 w-4" /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
